import classNames from 'classnames'
import React, { ChangeEventHandler, forwardRef } from 'react'
import { isFirefox } from 'react-device-detect'
import { isNullOrWhiteSpace } from '../../utilities/string'

export type RadioButtonProps = {
  additionalClassName?: string
  checked: boolean
  id?: string
  label: string
  name: string
  onBlur?: ChangeEventHandler<HTMLInputElement>
  onChange?: ChangeEventHandler<HTMLInputElement>
  required?: boolean
  tooltip?: string
  validation?: true | string
  value: string
  theme?: 'light' | 'dark'
}

export const RadioButton = forwardRef<HTMLInputElement, RadioButtonProps>(
  (
    {
      additionalClassName,
      checked,
      id,
      label,
      onChange,
      tooltip,
      validation,
      theme = 'light',
      ...inputProps
    },
    ref
  ) => {
    let className = classNames(
      'relative inline-flex justify-center items-center group transition-hover cursor-pointer',
      'px-10 py-7',
      'font-t-bold text-t-100',
      'border rounded',
      {
        'focus-within:ring focus-within:outline-none': isFirefox,
        '[&:has(:focus-visible)]:ring': !isFirefox,
      },
      {
        'text-secondary-darkest bg-neutral': theme === 'light',
        'text-white bg-transparent border-white': theme === 'dark' && !checked,
        'text-primary bg-white border-white': theme === 'dark' && checked,
      },
      additionalClassName
    )

    if (validation === true && checked) {
      className = classNames(className, 'border-success', {
        'focus-within:ring-success/40': isFirefox,
        '[&:has(:focus-visible)]:ring-success/40': !isFirefox,
      })
    } else if (validation !== true && !isNullOrWhiteSpace(validation)) {
      className = classNames(className, 'border-secondary', {
        'focus-within:ring-error/40': isFirefox,
        '[&:has(:focus-visible)]:ring-error/40': !isFirefox,
      })
    } else {
      className = classNames(className, {
        'border-primary': checked,
        'border-neutral-lighter-alt hover:border-primary': !checked && theme === 'light',
        'border-neutral-lighter-alt hover:bg-white hover:text-primary':
          !checked && theme === 'dark',
        'focus-within:ring-primary/40': isFirefox,
        '[&:has(:focus-visible)]:ring-primary/40': !isFirefox,
      })
    }

    return (
      <label className={className} title={tooltip}>
        <input
          id={id}
          {...inputProps}
          className="appearance-none opacity-0 absolute focus:outline-none"
          defaultChecked={checked}
          onChange={onChange}
          ref={ref}
          type="radio"
        />

        <span
          className={classNames(
            'relative inline-flex justify-center items-center w-15 h-15 mr-10 rounded-full overflow-hidden transition-all duration-200',
            {
              'bg-success': validation === true && checked,
              'bg-primary': validation !== true && checked,
              'bg-neutral-lighter-alt': !checked && theme === 'light',
              'bg-primary border-white border-2': !checked && theme === 'dark',
            }
          )}
        >
          <span
            className={classNames(
              'rounded-full transition-all duration-200 w-7 h-7',
              checked ? 'opacity-100' : 'opacity-0',
              {
                'bg-neutral w-7 h-7': checked && theme === 'light',
                'bg-white w-7 h-7': checked && theme === 'dark',
              }
            )}
          ></span>
        </span>

        <span className="select-none">{label}</span>
      </label>
    )
  }
)
